/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type SimpleChanges, ChangeDetectorRef, Component, Injector, Input } from '@angular/core';
import { type ColumnDef, type TableConfig, I18nService, TableAbstract } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { ShareTypeE } from 'app/feature/share/global-search/global-infigo/const';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { type Dtree, type FileSystem, type FileSystemResourceCIFS, type FileSystemResourceNFS } from '../../../../../../../common/types/mo';
import { InfigoService } from '../../../infigo.service';

@Component({
    selector: 'app-file-share-table',
    templateUrl: './file-share-table.component.html',
})
export class FileShareTableComponent extends TableAbstract<any> implements OnChanges {
    @Input() dtreeData: Dtree;
    @Input() fsData: FileSystem;
    @Input() shareType;
    @Input() updateLoading;
    @Input() checkOnlyShowFsShare;
    loading = true;
    $destroy = new Subject();
    // 表格行配置对象
    tableColumnDefs: ColumnDef[];
    // 表格总配置对象
    tableConfig: TableConfig;
    fileSystem = true;
    shareData: Array<FileSystemResourceCIFS | FileSystemResourceNFS> = [];
    usageValue = this.infigoService.usageValue;
    homedirOpts: SelectOption[] = [
        {
            label: this.i18n.get('common.term_No_label'),
            value: ShareTypeE.Normal,
        },
        {
            label: this.i18n.get('common.term_yes_label'),
            value: ShareTypeE.Homedir,
        },
    ];

    constructor(private i18n: I18nService,
        private infigoService: InfigoService,
        private changeDetectorRef: ChangeDetectorRef,
        injector: Injector) {
        super(injector);
    }

    ngOnChanges(changes: SimpleChanges): void {
        const { updateLoading } = changes;
        if (!updateLoading || updateLoading && !updateLoading.currentValue) {
            this.loading = true;
            this.shareType === 'NFS' ?
                this.handleNFSOrCIFSShareData('NFSShare.getBatch') :
                this.handleNFSOrCIFSShareData('CIFSShare.getBatch');
        }
    }

    initBtns(): void {
    }

    initTable(): void {
        const nfsColumnDefs: ColumnDef[] = [

            {
                field: 'NAME',
                header: this.i18n.get('service.share_term_shareName_lab'),
                filterType: 'search',
            },
            {
                field: 'SHAREPATH',
                header: this.i18n.get('service.share_term_sharePath_lab'),
                filterType: 'search',
                sortable: true,
            },
            {
                field: 'ID',
                header: this.i18n.get('common.ID_column'),
                hidden: true,
                filterType: 'equalSearch',
                sortable: true,
            },
        ];

        const cifsColumnDefs: ColumnDef[] = [
            {
                field: 'NAME',
                header: this.i18n.get('service.share_term_shareName_lab'),
                filterType: 'search',
                sortable: true,
            },
            {
                field: 'ID',
                header: this.i18n.get('common.ID_column'),
                hidden: true,
                filterType: 'equalSearch',
                sortable: true,
            },
            {
                field: 'SHAREPATH',
                header: this.fileSystem ?
                    this.i18n.get('service.share_term_sharePath_lab') :
                    this.i18n.get('service.dtree_task_create_fullPath_lab'),
                className: ['table-cell-text-pre'],
                filterType: 'search',
                sortable: true,
            },
            {
                field: 'subType',
                header: this.i18n.get('service.share_term_homedirShare_col'),
                filterType: 'dropdown',
                ignore: Boolean(this.fileSystem),
                options: this.homedirOpts,
                render: (value) => value === ShareTypeE.Homedir ?
                    this.i18n.get('common.term_yes_label') :
                    this.i18n.get('common.term_No_label'),
            },
            {
                field: 'ENABLENOTIFY',
                header: this.i18n.get('service.share_term_createCIFS_notify_tip'),
                hidden: true,
                render: (value) => value === 'true' ?
                    this.i18n.get('common.enabled_switch_column') :
                    this.i18n.get('common.disabled_switch_column'),
            },
            {
                field: 'ENABLECA',
                header: this.i18n.get('service.share_term_createCIFSHomedir_CA_tip'),
                hidden: true,
                render: (value) => value === 'true' ?
                    this.i18n.get('common.enabled_switch_column') :
                    this.i18n.get('common.disabled_switch_column'),
            },
            {
                field: 'ABEENABLE',
                header: this.i18n.get('service.share_term_createCIFS_enableABE_tip'),
                hidden: true,
                render: (value) => value === 'true' ?
                    this.i18n.get('common.enabled_switch_column') :
                    this.i18n.get('common.disabled_switch_column'),
            },
        ];

        this.tableColumnDefs = this.shareType === 'NFS' ? nfsColumnDefs : cifsColumnDefs;

        this.tableConfig = {
            dataKey: 'ID',
            motion: false,
            rows: 5,
        };
    }

    handleNFSOrCIFSShareData(url: string): void {
        if (this.fsData) {
            this.infigoService.getNFSOrCIFSShareData(url, this.fsData, this.dtreeData)
                .pipe(takeUntil(this.$destroy))
                .subscribe((value) => {
                    this.loading = false;
                    this.shareData = value.data;
                    this.changeDetectorRef.detectChanges();
                }, () => {
                    this.shareData = [];
                    this.loading = false;
                });
        } else {
            this.shareData = [];
            this.loading = false;
        }
    }
}
